import { supabase } from '../lib/supabase';
import securityService from './securityService';

/**
 * Security Monitoring Service
 * Real-time security monitoring and alerting
 */

class SecurityMonitoringService {
  constructor() {
    this.alerts = [];
    this.metrics = {
      authAttempts: 0,
      failedLogins: 0,
      suspiciousActivity: 0,
      blockedRequests: 0,
      xssAttempts: 0,
      sqlInjectionAttempts: 0
    };
    this.isMonitoring = false;
    this.monitoringInterval = null;
  }

  /**
   * Start security monitoring
   */
  startMonitoring() {
    if (this.isMonitoring) return;

    this.isMonitoring = true;
    
    // Monitor security events every 30 seconds
    this.monitoringInterval = setInterval(() => {
      this.checkSecurityMetrics();
    }, 30000);

    console.log('Security monitoring started');
  }

  /**
   * Stop security monitoring
   */
  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    this.isMonitoring = false;
    console.log('Security monitoring stopped');
  }

  /**
   * Check security metrics
   */
  async checkSecurityMetrics() {
    try {
      // Check for suspicious patterns
      await this.detectAnomalies();
      
      // Check rate limits
      await this.checkRateLimits();
      
      // Validate active sessions
      await this.validateActiveSessions();
      
    } catch (error) {
      console.error('Security monitoring error:', error);
    }
  }

  /**
   * Detect anomalies in security events
   */
  async detectAnomalies() {
    try {
      const { data: recentEvents } = await supabase?.from('security_audit_log')?.select('*')?.gte('timestamp', new Date(Date.now() - 3600000)?.toISOString())?.order('timestamp', { ascending: false })?.limit(100);

      if (!recentEvents) return;

      // Detect multiple failed login attempts
      const failedLogins = recentEvents?.filter(e => 
        e?.event_type === 'failed_login'
      );

      if (failedLogins?.length > 10) {
        this.createAlert('high', 'Multiple failed login attempts detected', {
          count: failedLogins?.length,
          timeWindow: '1 hour'
        });
      }

      // Detect XSS attempts
      const xssAttempts = recentEvents?.filter(e => 
        e?.event_type === 'xss_attempt'
      );

      if (xssAttempts?.length > 5) {
        this.createAlert('critical', 'Multiple XSS attempts detected', {
          count: xssAttempts?.length,
          timeWindow: '1 hour'
        });
      }

      // Detect SQL injection attempts
      const sqlAttempts = recentEvents?.filter(e => 
        e?.event_type === 'sql_injection_attempt'
      );

      if (sqlAttempts?.length > 5) {
        this.createAlert('critical', 'Multiple SQL injection attempts detected', {
          count: sqlAttempts?.length,
          timeWindow: '1 hour'
        });
      }

    } catch (error) {
      console.error('Anomaly detection error:', error);
    }
  }

  /**
   * Check rate limits across the application
   */
  async checkRateLimits() {
    // This would integrate with your rate limiting system
    // For now, we'll track metrics
    this.metrics.rateLimitChecks = (this.metrics?.rateLimitChecks || 0) + 1;
  }

  /**
   * Validate active sessions
   */
  async validateActiveSessions() {
    try {
      const validation = await securityService?.validateSession();
      
      if (!validation?.valid) {
        this.createAlert('medium', 'Invalid session detected', {
          error: validation?.error
        });
      }
    } catch (error) {
      console.error('Session validation error:', error);
    }
  }

  /**
   * Create security alert
   */
  createAlert(severity, message, details = {}) {
    const alert = {
      id: crypto.randomUUID(),
      severity,
      message,
      details,
      timestamp: new Date()?.toISOString(),
      acknowledged: false
    };

    this.alerts?.push(alert);

    // Keep only last 100 alerts
    if (this.alerts?.length > 100) {
      this.alerts = this.alerts?.slice(-100);
    }

    // Log to database
    this.logAlert(alert);

    // Trigger notification for critical alerts
    if (severity === 'critical') {
      this.notifyCriticalAlert(alert);
    }

    return alert;
  }

  /**
   * Log alert to database
   */
  async logAlert(alert) {
    try {
      await supabase?.from('security_alerts')?.insert({
        severity: alert?.severity,
        message: alert?.message,
        details: alert?.details,
        timestamp: alert?.timestamp
      });
    } catch (error) {
      console.error('Failed to log alert:', error);
    }
  }

  /**
   * Notify critical alert
   */
  notifyCriticalAlert(alert) {
    // This would integrate with your notification system
    console.error('CRITICAL SECURITY ALERT:', alert);
    
    // Could send email, SMS, or push notification
    // For now, we'll just log to console
  }

  /**
   * Get recent alerts
   */
  getRecentAlerts(limit = 20) {
    return this.alerts?.slice(-limit)?.reverse();
  }

  /**
   * Acknowledge alert
   */
  acknowledgeAlert(alertId) {
    const alert = this.alerts?.find(a => a?.id === alertId);
    if (alert) {
      alert.acknowledged = true;
      alert.acknowledgedAt = new Date()?.toISOString();
    }
  }

  /**
   * Get security metrics
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Track security event
   */
  trackEvent(eventType) {
    switch (eventType) {
      case 'auth_attempt':
        this.metrics.authAttempts++;
        break;
      case 'failed_login':
        this.metrics.failedLogins++;
        break;
      case 'suspicious_activity':
        this.metrics.suspiciousActivity++;
        break;
      case 'blocked_request':
        this.metrics.blockedRequests++;
        break;
      case 'xss_attempt':
        this.metrics.xssAttempts++;
        break;
      case 'sql_injection_attempt':
        this.metrics.sqlInjectionAttempts++;
        break;
    }
  }

  /**
   * Scan for vulnerabilities
   */
  async scanVulnerabilities() {
    const vulnerabilities = [];

    // Check for weak passwords
    const weakPasswordCheck = await this.checkWeakPasswords();
    if (weakPasswordCheck?.found) {
      vulnerabilities?.push({
        type: 'weak_passwords',
        severity: 'medium',
        description: 'Users with weak passwords detected',
        count: weakPasswordCheck?.count
      });
    }

    // Check for inactive sessions
    const staleSessionCheck = await this.checkStaleSessions();
    if (staleSessionCheck?.found) {
      vulnerabilities?.push({
        type: 'stale_sessions',
        severity: 'low',
        description: 'Inactive sessions detected',
        count: staleSessionCheck?.count
      });
    }

    // Check for missing security headers
    const headersCheck = this.checkSecurityHeaders();
    if (headersCheck?.missing?.length > 0) {
      vulnerabilities?.push({
        type: 'missing_headers',
        severity: 'medium',
        description: 'Security headers not configured',
        missing: headersCheck?.missing
      });
    }

    return vulnerabilities;
  }

  /**
   * Check for weak passwords
   */
  async checkWeakPasswords() {
    // This would require password strength tracking
    // For now, return placeholder
    return { found: false, count: 0 };
  }

  /**
   * Check for stale sessions
   */
  async checkStaleSessions() {
    try {
      const { data: sessions } = await supabase?.from('user_sessions')?.select('*')?.lt('last_activity', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString());

      return {
        found: sessions && sessions?.length > 0,
        count: sessions?.length || 0
      };
    } catch (error) {
      return { found: false, count: 0 };
    }
  }

  /**
   * Check security headers
   */
  checkSecurityHeaders() {
    const requiredHeaders = [
      'Content-Security-Policy',
      'X-Frame-Options',
      'X-Content-Type-Options',
      'Referrer-Policy'
    ];

    // In a real implementation, this would check actual response headers
    // For now, return placeholder
    return {
      missing: [],
      configured: requiredHeaders
    };
  }

  /**
   * Generate security report
   */
  async generateSecurityReport() {
    const vulnerabilities = await this.scanVulnerabilities();
    const recentAlerts = this.getRecentAlerts(50);
    const metrics = this.getMetrics();

    return {
      timestamp: new Date()?.toISOString(),
      vulnerabilities,
      alerts: {
        total: recentAlerts?.length,
        critical: recentAlerts?.filter(a => a?.severity === 'critical')?.length,
        high: recentAlerts?.filter(a => a?.severity === 'high')?.length,
        medium: recentAlerts?.filter(a => a?.severity === 'medium')?.length,
        low: recentAlerts?.filter(a => a?.severity === 'low')?.length
      },
      metrics,
      recommendations: this.generateRecommendations(vulnerabilities, metrics)
    };
  }

  /**
   * Generate security recommendations
   */
  generateRecommendations(vulnerabilities, metrics) {
    const recommendations = [];

    if (metrics?.failedLogins > 50) {
      recommendations?.push({
        priority: 'high',
        action: 'Implement account lockout after multiple failed login attempts'
      });
    }

    if (metrics?.xssAttempts > 10) {
      recommendations?.push({
        priority: 'critical',
        action: 'Review and strengthen XSS protection mechanisms'
      });
    }

    if (vulnerabilities?.some(v => v?.type === 'weak_passwords')) {
      recommendations?.push({
        priority: 'medium',
        action: 'Enforce stronger password policies'
      });
    }

    return recommendations;
  }
}

export default new SecurityMonitoringService();